import React from 'react';
import AnimatedSection from '../../../components/base/AnimatedSection';

export default function FunnelsSection() {
  /** Scrolls smoothly to the analysis form section.
   *  If the element cannot be found, a warning is logged instead of throwing.
   */
  const scrollToForm = () => {
    const formSection = document.getElementById('analiz-formu');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.warn('Element with id "analiz-formu" not found.');
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white via-gold-50/30 to-white relative overflow-hidden" id="funnels">
      {/* Decorative Background Elements */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <AnimatedSection animation="fadeUp">
          <div className="text-center mb-20">
            <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-accent/10 to-accent/5 text-accent rounded-full text-sm font-semibold mb-6 border border-accent/20">
              <i className="ri-stack-line text-lg"></i>
              Çözümlerimiz
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              İşletmenize Özel <span className="text-accent">Çözüm Paketleri</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
              İhtiyaçlarınıza göre özelleştirilmiş iki farklı yaklaşım ile operasyonel verimliliği artırın ve gelir potansiyelinizi maksimize edin
            </p>
          </div>
        </AnimatedSection>

        {/* Funnel A - Operasyonel Sistem */}
        <AnimatedSection animation="fadeUp" delay={100}>
          <div className="mb-16 bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left Side - Visual */}
              <div className="relative bg-gradient-to-br from-primary via-primary-dark to-primary p-12 flex items-center justify-center overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/5 rounded-full blur-2xl"></div>
                
                <div className="relative z-10 w-full h-full flex items-center justify-center">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/7ae509a508d8d856969da772e6d3c3a8.jpg"
                    alt="PMS System Dashboard"
                    className="w-full h-full object-cover rounded-2xl shadow-2xl"
                  />
                </div>
              </div>

              {/* Right Side - Content */}
              <div className="p-12 flex flex-col justify-center">
                <h3 className="text-3xl font-bold text-primary mb-4">
                  Operasyonel Sistem Mimarisi
                </h3>
                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                  Manuel süreçleri azaltan, rezervasyon akışını tutarlı hale getiren ve operasyonel verimliliği artıran entegre sistem çözümü.
                </p>

                <div className="mb-8">
                  <h4 className="text-sm font-bold text-accent uppercase tracking-wider mb-5 flex items-center gap-2">
                    <i className="ri-user-star-line"></i>
                    Kimler İçin Uygundur
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-gold-50/50 rounded-xl border border-gold-100">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent/10 text-accent flex-shrink-0">
                        <i className="ri-hotel-line text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-primary mb-1">PMS Sistemi Olmayan</div>
                        <div className="text-sm text-gray-600">Manuel süreçlerle çalışan veya mevcut sistemi yetersiz olan işletmeler</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-gold-50/50 rounded-xl border border-gold-100">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent/10 text-accent flex-shrink-0">
                        <i className="ri-links-line text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-primary mb-1">Kanal Entegrasyonu Eksik</div>
                        <div className="text-sm text-gray-600">OTA platformları ile senkronizasyon sorunu yaşayan tesisler</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-gold-50/50 rounded-xl border border-gold-100">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent/10 text-accent flex-shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-primary mb-1">Operasyonel Yük Fazla</div>
                        <div className="text-sm text-gray-600">Günlük işlemlerde çok fazla zaman kaybeden ekipler</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-sm font-bold text-accent uppercase tracking-wider mb-5 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    Elde Edeceğiniz Sonuçlar
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <i className="ri-check-double-line text-accent text-xl"></i>
                      <span className="text-sm font-medium text-gray-700">Entegre PMS Kurulumu</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <i className="ri-check-double-line text-accent text-xl"></i>
                      <span className="text-sm font-medium text-gray-700">OTA Senkronizasyonu</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <i className="ri-check-double-line text-accent text-xl"></i>
                      <span className="text-sm font-medium text-gray-700">Otomatik Raporlama</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <i className="ri-check-double-line text-accent text-xl"></i>
                      <span className="text-sm font-medium text-gray-700">7/24 AI Destek</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={scrollToForm}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-accent to-accent-dark hover:from-accent-dark hover:to-accent text-primary font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-xl shadow-accent/30 hover:shadow-2xl hover:shadow-accent/40 hover:-translate-y-0.5"
                >
                  <i className="ri-play-circle-line text-xl"></i>
                  Sistem Analizini Başlat
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Decorative Divider */}
        <div className="relative py-12">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t-2 border-dashed border-gray-200"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-6 py-3 rounded-full border-2 border-accent/20 text-accent font-semibold text-sm shadow-lg">
              <i className="ri-arrow-down-line mr-2"></i>
              veya
              <i className="ri-arrow-down-line ml-2"></i>
            </span>
          </div>
        </div>

        {/* Funnel B - Ticari Büyüme */}
        <AnimatedSection animation="fadeUp" delay={200}>
          <div id="funnel-ticari-buyume" className="mb-16 bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left Side - Visual */}
              <div className="relative bg-gradient-to-br from-accent/5 via-gold-500/5 to-accent-light/5 p-12 flex items-center justify-center overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full blur-2xl"></div>
                
                <div className="relative z-10 w-full h-full flex items-center justify-center">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20hotel%20revenue%20management%20consultant%20analyzing%20data%20on%20multiple%20screens%20showing%20booking%20trends%20pricing%20strategies%20and%20occupancy%20rates%20modern%20office%20environment%20with%20charts%20and%20graphs%20business%20analytics%20navy%20blue%20and%20gold%20accents%20clean%20professional%20atmosphere%20high%20quality%20realistic%20photography&width=800&height=900&seq=funnel-b-consulting-visual&orientation=portrait"
                    alt="Revenue Management Consulting"
                    className="w-full h-full object-cover rounded-2xl shadow-2xl"
                  />
                </div>
              </div>

              {/* Right Side - Content (Reversed Order) */}
              <div className="p-12 flex flex-col justify-center order-2 lg:order-1">
                <h3 className="text-3xl font-bold text-primary mb-4">
                  Uçtan Uca Ticari Büyüme Sistemi
                </h3>
                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                  Hızlı büyüme ve tam kontrol için ticari sistem mimarisi. Revenue management, merkezi yönetim ve stratejik danışmanlık ile maksimum verimlilik.
                </p>

                <div className="mb-8">
                  <h4 className="text-sm font-bold text-accent uppercase tracking-wider mb-5 flex items-center gap-2">
                    <i className="ri-user-star-line"></i>
                    Kimler İçin Uygundur
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-gold-50/50 rounded-xl border border-gold-100">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent/10 text-accent flex-shrink-0">
                        <i className="ri-building-line text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-primary mb-1">Çok Şubeli İşletmeler</div>
                        <div className="text-sm text-gray-600">Birden fazla lokasyonu merkezi olarak yönetmek isteyen otel zincirleri</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-gold-50/50 rounded-xl border border-gold-100">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent/10 text-accent flex-shrink-0">
                        <i className="ri-line-chart-line text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-primary mb-1">Yatırımcı & Portfolyo Yöneticileri</div>
                        <div className="text-sm text-gray-600">Tüm portföyü tek panelden izlemek ve optimize etmek isteyenler</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-gold-50/50 rounded-xl border border-gold-100">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent/10 text-accent flex-shrink-0">
                        <i className="ri-rocket-line text-lg"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-primary mb-1">Hızla Büyüyen Tesisler</div>
                        <div className="text-sm text-gray-600">Ölçeklenebilir altyapı ve stratejik büyüme planı gerektiren işletmeler</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-sm font-bold text-accent uppercase tracking-wider mb-5 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    Elde Edeceğiniz Sonuçlar
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-accent/5 to-transparent rounded-lg">
                      <i className="ri-check-double-line text-accent text-xl"></i>
                      <span className="text-sm font-medium text-gray-700">Revenue Management</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-accent/5 to-transparent rounded-lg">
                      <i className="ri-check-double-line text-accent text-xl"></i>
                      <span className="text-sm font-medium text-gray-700">Merkezi Yönetim</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-accent/5 to-transparent rounded-lg">
                      <i className="ri-check-double-line text-accent text-xl"></i>
                      <span className="text-sm font-medium text-gray-700">Ölçeklenebilir Altyapı</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-accent/5 to-transparent rounded-lg">
                      <i className="ri-check-double-line text-accent text-xl"></i>
                      <span className="text-sm font-medium text-gray-700">Stratejik Danışmanlık</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={scrollToForm}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40 hover:-translate-y-0.5"
                >
                  <i className="ri-presentation-line text-xl"></i>
                  Full Stack Sunum Talep Et
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* How It Works - Process Timeline */}
        <AnimatedSection animation="fadeUp" delay={300}>
          <div className="mt-24 bg-gradient-to-br from-primary via-primary-dark to-primary rounded-3xl p-12 relative overflow-hidden" id="process">
            <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/5 rounded-full blur-2xl"></div>

            <div className="relative z-10">
              <div className="text-center mb-16">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent/20 text-accent rounded-full text-sm font-semibold mb-6 border border-accent/30">
                  <i className="ri-route-line text-lg"></i>
                  Süreç
                </span>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Nasıl Çalışır?
                </h3>
                <p className="text-white/70 text-lg max-w-2xl mx-auto">
                  4 adımda işletmenizi dönüştürüyoruz
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Step 1 */}
                <div className="relative">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all h-full">
                    <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-accent text-primary font-bold text-2xl mb-4 shadow-lg">
                      1
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-search-eye-line text-accent text-2xl"></i>
                      <h4 className="text-xl font-bold text-white">Analiz</h4>
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed">
                      Mevcut durumunuzu detaylı analiz ediyor, sorun noktalarını tespit ediyoruz
                    </p>
                  </div>
                  {/* Arrow */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-accent/30 text-3xl">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all h-full">
                    <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-accent text-primary font-bold text-2xl mb-4 shadow-lg">
                      2
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-lightbulb-line text-accent text-2xl"></i>
                      <h4 className="text-xl font-bold text-white">Planlama</h4>
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed">
                      Size özel çözüm planı hazırlıyor, yol haritasını belirliyoruz
                    </p>
                  </div>
                  {/* Arrow */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-accent/30 text-3xl">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all h-full">
                    <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-accent text-primary font-bold text-2xl mb-4 shadow-lg">
                      3
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-tools-line text-accent text-2xl"></i>
                      <h4 className="text-xl font-bold text-white">Kurulum</h4>
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed">
                      Sistemleri devreye alıyor, ekibinizi eğitiyor, entegrasyonları tamamlıyoruz
                    </p>
                  </div>
                  {/* Arrow */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-accent/30 text-3xl">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="relative">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all h-full">
                    <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-accent text-primary font-bold text-2xl mb-4 shadow-lg">
                      4
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-line-chart-line text-accent text-2xl"></i>
                      <h4 className="text-xl font-bold text-white">Optimizasyon</h4>
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed">
                      Sürekli izliyor, optimize ediyor ve büyümenizi destekliyoruz
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
